import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return (
        <div className={`page about-page ${isLoaded ? 'loaded' : ''}`}>
            {/* Hero */}
            <section className="about-hero">
                <div className="container">
                    <span className="label">About <span className="brand-name">BAi Systems</span></span>
                    <h1>Technology that disappears into architecture.<span className="tm">™</span></h1>
                </div>
            </section>

            {/* Story */}
            <section className="story-section">
                <div className="container">
                    <div className="story-content">
                        <p className="story-lead">
                            We started BAi Systems because we saw a gap in the market:
                            brilliant architects designing extraordinary spaces, and technology
                            integrators undermining those designs with visible equipment and
                            poor documentation.
                        </p>
                        <p>
                            The problem wasn't the technology—it was the approach. Most integrators
                            come from the AV or security world, where the equipment IS the product.
                            In luxury residential construction, the equipment should be invisible.
                            The experience is the product.
                        </p>
                        <p>
                            We built our practice on a belief: that building automation
                            deserves the same engineering rigor as HVAC or structural systems.
                            That means proper documentation, trade coordination, and a
                            methodology that produces predictable results.
                        </p>
                        <p>
                            Today, we work with architects, builders, and developers who share
                            that standard. Our documentation has become a competitive advantage
                            for our partners—and a differentiator for the properties they deliver.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="values-section">
                <div className="container">
                    <span className="label">What We Believe</span>
                    <h2>Principles that guide every project</h2>

                    <div className="values-grid">
                        <div className="value">
                            <h3>Invisible by design</h3>
                            <p>
                                If someone notices the technology, we've failed. Our
                                systems enhance the experience of a space without
                                announcing their presence.
                            </p>
                        </div>
                        <div className="value">
                            <h3>Documentation is a deliverable</h3>
                            <p>
                                Engineering packages aren't an afterthought—they're
                                a primary output. They protect architects, builders,
                                and homeowners for the lifetime of the system.
                            </p>
                        </div>
                        <div className="value">
                            <h3>Product-agnostic engineering</h3>
                            <p>
                                We specify the right solution for each project, not
                                the products that earn us the highest margin. Our
                                loyalty is to the architecture, not a brand.
                            </p>
                        </div>
                        <div className="value">
                            <h3>Transparency in everything</h3>
                            <p>
                                Clear pricing, honest timelines, documented processes.
                                We'd rather lose a project than win it through ambiguity.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process Section - integrated from v1 */}
            <section className="about-process-section">
                <div className="container">
                    <span className="label">Our Process</span>
                    <h2>Engineering excellence from concept to commissioning</h2>
                    <p className="about-process-intro">
                        Every project follows our proven methodology—designed to eliminate
                        surprises and deliver predictable, documented results.
                    </p>

                    <div className="about-process-timeline">
                        <div className="about-process-step">
                            <div className="about-step-marker">
                                <span className="about-step-number">01</span>
                            </div>
                            <div className="about-step-content">
                                <div className="about-step-phase">Discovery</div>
                                <h3>Understanding Your Vision</h3>
                                <p>We begin by understanding the architecture, the occupants, and the desired experience. No product discussions—just vision and intent.</p>
                            </div>
                        </div>

                        <div className="about-process-step">
                            <div className="about-step-marker">
                                <span className="about-step-number">02</span>
                            </div>
                            <div className="about-step-content">
                                <div className="about-step-phase">Engineering</div>
                                <h3>System Design</h3>
                                <p>Our engineers create comprehensive documentation: load calculations, wire schedules, device locations, control logic, and coordination drawings.</p>
                            </div>
                        </div>

                        <div className="about-process-step">
                            <div className="about-step-marker">
                                <span className="about-step-number">03</span>
                            </div>
                            <div className="about-step-content">
                                <div className="about-step-phase">Coordination</div>
                                <h3>Trade Alignment</h3>
                                <p>We coordinate electrical, HVAC and low-voltage requirements before the trades reach the same wall.</p>
                            </div>
                        </div>

                        <div className="about-process-step">
                            <div className="about-step-marker">
                                <span className="about-step-number">04</span>
                            </div>
                            <div className="about-step-content">
                                <div className="about-step-phase">Installation</div>
                                <h3>Precision Execution</h3>
                                <p>Our vetted installation partners execute the engineered design exactly as specified, with quality checkpoints throughout.</p>
                            </div>
                        </div>

                        <div className="about-process-step">
                            <div className="about-step-marker">
                                <span className="about-step-number">05</span>
                            </div>
                            <div className="about-step-content">
                                <div className="about-step-phase">Commissioning</div>
                                <h3>Bringing It to Life</h3>
                                <p>Scenes programmed, sensors checked and automation tested against the design. Commissioning records document the result.</p>
                            </div>
                        </div>

                        <div className="about-process-step">
                            <div className="about-step-marker">
                                <span className="about-step-number">06</span>
                            </div>
                            <div className="about-step-content">
                                <div className="about-step-phase">Handoff</div>
                                <h3>Documentation & Training</h3>
                                <p>Comprehensive as-built documentation, user guides, maintenance schedules, and personalized training for every occupant.</p>
                            </div>
                        </div>
                    </div>

                    {/* Documentation Differentiator */}
                    <div className="about-process-differentiator">
                        <div className="diff-content">
                            <h3>Documentation as a Differentiator</h3>
                            <p>
                                When other integrators hand over a messy folder of PDFs, we deliver
                                portfolio-worthy engineering packages. Our documentation protects
                                architects, builders, and homeowners for the lifetime of the system.
                            </p>
                        </div>
                        <div className="diff-deliverables">
                            <h4>What you receive</h4>
                            <div className="deliverables-list">
                                <span>Technology Narrative</span>
                                <span>System Scope Document</span>
                                <span>Device Location Plans</span>
                                <span>Wire Schedules</span>
                                <span>User Guides</span>
                                <span>Warranty Summary</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Experience Stats */}
            <section className="experience-section">
                <div className="container">
                    <div className="experience-content">
                        <span className="label">Experience</span>
                        <h2>Built on real-world results</h2>
                        <p>
                            Founder Tyler Christensen brings more than thirteen years of experience
                            in software, automation and integration. BAi pairs that system-design
                            experience with vetted field partners who carry out the installation.
                        </p>
                        <p>
                            We've worked on everything from 3,000-square-foot renovations
                            to 30,000-square-foot new construction, and from single-family
                            homes to multi-residence developments.
                        </p>
                    </div>

                    <div className="experience-stats">
                        <div className="stat">
                            <span className="stat-number">13+</span>
                            <span className="stat-label">Founder’s experience</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">1000s</span>
                            <span className="stat-label">Systems delivered by our field partner</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">One</span>
                            <span className="stat-label">Design authority</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="about-cta">
                <div className="container">
                    <h2>Let's see if we're the right fit for your project.</h2>
                    <Link to="/contact" className="btn btn-primary">Start a conversation</Link>
                </div>
            </section>
        </div>
    );
};

export default About;
